import { ToastContext } from "context/ToastContext";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { TabView, TabPanel } from "primereact/tabview";
import { useContext, useState, useEffect } from "react";
import {
  useCreateMoneyTransferRequestMutation,
  useGetMoneyTransferRequestsByOrderQuery,
} from "store/queries/moneyTransfers";
import styled from "styled-components";
import { ITransfer } from "types/MoneyTransfers";
import { IOrder } from "types/Order/Order";

import { MoneyTransferRequestsList } from "./components/MoneyTransferRequestStatus";
import { SingleTransferForm } from "./components/SingleTransferForm";
import { mapFormToRequestBody } from "./utils/helpers";

const StyledDialog = styled(Dialog)`
  .p-dialog-title {
    color: #5b4dfd;
  }
`;

export const StyledButton = styled(Button)`
  gap: ${sizer(2)};
  margin-bottom: ${sizer(4)};

  span:first-child {
    font-size: ${sizer(3)};
  }
`;

export function MoneyTransferDialog({
  showDialog,
  setShowDialog,
  order,
  transfers,
  setTransfers,
  onUpdateOrder,
  defaultTabIndex = 0,
}: {
  showDialog: boolean;
  setShowDialog: any;
  order: IOrder;
  transfers: ITransfer[];
  setTransfers: any;
  onUpdateOrder: () => void;
  defaultTabIndex?: number;
}) {
  const [notes, setNotes] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(defaultTabIndex);
  const { current: toastElement } = useContext(ToastContext);
  const [useCreateMoneyTransferRequest] =
    useCreateMoneyTransferRequestMutation();

  // Fetch transfer history when dialog opens
  const { data: requestsData, refetch: refetchRequests } =
    useGetMoneyTransferRequestsByOrderQuery(order.id, {
      skip: !showDialog,
    });

  // Check for any existing transfers
  const existingTransfers = requestsData?.data || [];

  // Refetch data when dialog opens
  useEffect(() => {
    if (showDialog) {
      refetchRequests();
    }
  }, [showDialog, refetchRequests]);
  const handleCreateMoneyTransfers = async () => {
    const requestBody = mapFormToRequestBody({
      orderId: order.id,
      moneyTransfers: transfers,
      note: notes?.trim()?.length ? notes.trim() : undefined,
    });
    setLoading(true);

    // Always use asynchronous money transfer request endpoint
    useCreateMoneyTransferRequest(requestBody)
      .unwrap()
      .then(async (response) => {
        if (response.data?.error) {
          toastElement?.show({
            severity: "error",
            summary: "Error",
            detail: response.data.error,
          });
          setLoading(false);
          return;
        }

        // Show success message with scheduling information
        const { totalRequests, firstScheduledAt, lastScheduledAt, message } =
          response.data;
        const now = new Date();
        const firstDelay = Math.ceil(
          (new Date(firstScheduledAt).getTime() - now.getTime()) / (1000 * 60)
        );

        let detail = message;
        if (totalRequests > 1) {
          const lastDelay = Math.ceil(
            (new Date(lastScheduledAt).getTime() - now.getTime()) / (1000 * 60)
          );
          detail = `${totalRequests} money transfer requests created. First processes ${
            firstDelay > 1 ? `in ${firstDelay} minutes` : "shortly"
          }, last processes in ${lastDelay} minutes.`;
        } else if (firstDelay > 1) {
          detail = `Money transfer request created and scheduled to process in ${firstDelay} minutes`;
        } else {
          detail =
            "Money transfer request created and will be processed shortly";
        }

        toastElement?.show({
          severity: "success",
          summary: `${totalRequests} Money Transfer Request${
            totalRequests === 1 ? "" : "s"
          } Created`,
          detail,
          life: 8000, // Longer display time for multiple requests
        });
        setLoading(false);
        setShowDialog(false);
        setNotes("");
        onUpdateOrder();
        // Refetch transfer requests after creating a new one
        refetchRequests();
      })
      .catch((error) => {
        setLoading(false);
        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail:
            error.data?.message || "Failed to create money transfer request",
        });
      });
  };

  const footerContent =
    activeTabIndex === 0 ? (
      <div className="flex justify-content-center">
        <Button
          label="Transfer"
          onClick={handleCreateMoneyTransfers}
          autoFocus
          loading={loading}
        />
      </div>
    ) : null;

  const handleAddAnotherTransfer = () => {
    setTransfers((prev: any) => [
      ...prev,
      {
        selectedPartyToTransferFrom: null,
        selectedPartyToTransferTo: null,
        selectedAmountToTransfer: 0,
      },
    ]);
  };

  const handleRemoveTransfer = (index: number) => {
    const transferCopy = [...transfers];
    transferCopy.splice(index, 1);
    setTransfers(transferCopy);
  };

  return (
    <StyledDialog
      header="Money Transfers"
      visible={showDialog}
      style={{ width: "60vw", minHeight: "600px" }}
      blockScroll
      onHide={() => {
        setShowDialog(false);
        setActiveTabIndex(defaultTabIndex);
        setTransfers([
          {
            selectedPartyToTransferFrom: null,
            selectedPartyToTransferTo: null,
            selectedAmountToTransfer: 0,
          },
        ]);
      }}
      footer={footerContent}
    >
      <TabView
        activeIndex={activeTabIndex}
        onTabChange={(e) => setActiveTabIndex(e.index)}
      >
        <TabPanel header="Create Transfer">
          <div className="flex flex-column">
            {existingTransfers.length > 0 && (
              <Message
                severity="info"
                text={`Note: There ${
                  existingTransfers.length === 1 ? "is" : "are"
                } ${existingTransfers.length} existing money transfer${
                  existingTransfers.length === 1 ? "" : "s"
                } for this order. Please check them before creating additional transfers.`}
                className="mb-3"
              />
            )}
            {transfers.map((transfer, i) => (
              <SingleTransferForm
                key={`${i}_order_transfer`}
                transfer={transfer}
                setTransfers={setTransfers}
                order={order}
                index={i}
                handleRemoveTransfer={handleRemoveTransfer}
                showRemoveButton={transfers.length > 1}
              />
            ))}
            <StyledButton
              data-testid="remove-transfer"
              icon="pi pi-plus-circle"
              className="p-button-text p-button-primary p-0 mb-0"
              style={{ alignSelf: "end" }}
              onClick={handleAddAnotherTransfer}
            >
              <p className="small-text">Add Another</p>
            </StyledButton>
            <div className="w-full mt-3" style={{ textAlign: "left" }}>
              <InputTextarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Notes"
                className="w-full"
                style={{ textAlign: "left", width: "100%" }}
              />
            </div>
          </div>
        </TabPanel>
        <TabPanel header="Money Transfer History">
          <MoneyTransferRequestsList orderId={order.id} />
        </TabPanel>
      </TabView>
    </StyledDialog>
  );
}
